<template>
  <div class="pc-form">
    <div class="pc-header">
      <div class="pc-header-left">
        <span class="pc-icon">
          <font-awesome-icon :icon="['fas', isBuilding ? 'building' : 'layer-group']" />
        </span>
        <span class="pc-title">{{ isBuilding ? "Add Building" : "Add Component" }}</span>
      </div>
      <button class="pc-close" type="button" :disabled="saving" aria-label="Close" @click="$emit('close')">
        <font-awesome-icon :icon="['fas', 'times']" />
      </button>
    </div>

    <div v-if="created" class="pc-body">
      <div class="pc-alert pc-alert--ok">
        <font-awesome-icon :icon="['fas', 'check-circle']" />
        <div>
          <div class="pc-alert-title">{{ isBuilding ? "Building added" : "Component added" }}</div>
          <div v-if="isBuilding">
            Building {{ created.fid }}<template v-if="created.uid">, with its first component
              ({{ created.uid }})</template>.
          </div>
          <div v-else>Component {{ created.uid }}, in building {{ created.fid }}.</div>
        </div>
      </div>

      <div v-if="created.warnings && created.warnings.length" class="pc-alert pc-alert--warn">
        <font-awesome-icon :icon="['fas', 'exclamation-triangle']" />
        <div>
          <div class="pc-alert-title">Saved, with notes</div>
          <ul class="pc-warn-list">
            <li v-for="(warning, i) in created.warnings" :key="i">{{ warning }}</li>
          </ul>
        </div>
      </div>
    </div>

    <div v-else class="pc-body">
      <div class="pc-current">
        <div class="pc-current-title">{{ projectTitle || `Presale project ${pid}` }}</div>
        <dl class="pc-current-grid">
          <dt>Project</dt>
          <dd>{{ pid }}</dd>
          <template v-if="!isBuilding">
            <dt>Building</dt>
            <dd>{{ buildingLabel }}</dd>
          </template>
        </dl>
        <p class="pc-help">
          {{ isBuilding
            ? "A second tower, phase or podium on this site. It becomes another project type collection on the project."
            : "Another unit type collection inside this building — a rental component over a strata podium, a retail base." }}
        </p>
      </div>

      <div class="pc-row--pair">
        <div v-for="field in primaryFields" :key="field.key">
          <label class="pc-label" :for="'pc-main-' + field.key">{{ field.label }}</label>
          <select v-if="field.options" :id="'pc-main-' + field.key" class="pc-input"
            :disabled="saving || loadingOptions" v-model="primary[field.key]">
            <option value="">{{ loadingOptions ? "Loading…" : "—" }}</option>
            <option v-for="option in options[field.options]" :key="option.id" :value="option.id">
              {{ option.label }}
            </option>
          </select>
          <!-- `step` is set only on the decimal fields; undefined leaves the
               attribute off, and a number input without it rejects anything
               that is not a whole number. -->
          <input v-else :id="'pc-main-' + field.key" class="pc-input" :type="field.type || 'text'"
            :step="field.step" :placeholder="field.placeholder || ''" :disabled="saving"
            v-model="primary[field.key]" />
        </div>
      </div>

      <!-- Adding a building can file its first component in the same step. Left
           untouched it writes none, and the project page then offers "+ Component"
           against the empty building. -->
      <div v-if="isBuilding" class="pc-section">
        <button type="button" class="pc-section-head" :aria-expanded="String(componentOpen)"
          @click="componentOpen = !componentOpen">
          <span>
            <span class="pc-section-title">First component</span>
            <span class="pc-section-note">Optional — the building can be added on its own</span>
          </span>
          <font-awesome-icon :icon="['fas', componentOpen ? 'chevron-up' : 'chevron-down']" />
        </button>

        <div v-if="componentOpen" class="pc-section-body">
          <div class="pc-row--pair">
            <div v-for="field in utcFields" :key="field.key">
              <label class="pc-label" :for="'pc-utc-' + field.key">{{ field.label }}</label>
              <select v-if="field.options" :id="'pc-utc-' + field.key" class="pc-input"
                :disabled="saving || loadingOptions" v-model="secondary[field.key]">
                <option value="">{{ loadingOptions ? "Loading…" : "—" }}</option>
                <option v-for="option in options[field.options]" :key="option.id" :value="option.id">
                  {{ option.label }}
                </option>
              </select>
              <input v-else :id="'pc-utc-' + field.key" class="pc-input" :type="field.type || 'text'"
                :step="field.step" :placeholder="field.placeholder || ''" :disabled="saving"
                v-model="secondary[field.key]" />
            </div>
          </div>
        </div>
      </div>

      <div v-if="error" class="pc-alert pc-alert--error">
        <font-awesome-icon :icon="['fas', 'exclamation-circle']" />
        <div>
          <div class="pc-alert-title">Could not save</div>
          <div>{{ error }}</div>
        </div>
      </div>
    </div>

    <div class="pc-footer">
      <div class="pc-footer-status">
        <span v-if="saving">Saving…</span>
        <span v-else-if="!created && isEmpty">Nothing filled in — it will be added blank.</span>
      </div>
      <div class="pc-footer-actions">
        <template v-if="created">
          <button type="button" class="pc-cancel-btn" @click="reset">Add another</button>
          <button type="button" class="pc-save-btn" @click="$emit('close')">Done</button>
        </template>
        <template v-else>
          <button type="button" class="pc-cancel-btn" :disabled="saving" @click="$emit('close')">Cancel</button>
          <button type="button" class="pc-save-btn" :disabled="saving" @click="save">
            <font-awesome-icon v-if="saving" :icon="['fas', 'spinner']" class="fa-spin" />
            {{ saving ? "Saving…" : isBuilding ? "Add building" : "Add component" }}
          </button>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { useProjectStore } from "../../store/ProjectStore";
import {
  PTC_FIELDS,
  UTC_FIELDS,
  compactFields,
  emptyFields,
} from "../../data/projectCollectionFields";

export default {
  name: "ProjectCollectionForm",
  props: {
    // The presale project both levels hang off.
    pid: { type: [Number, String], required: true },
    projectTitle: { type: String, default: "" },
    // "building" adds a project type collection to the project; "component"
    // adds a unit type collection to the building `fid` names.
    mode: {
      type: String,
      default: "building",
      validator: (value) => ["building", "component"].includes(value),
    },
    // Required in component mode: which building the component goes into. The
    // server refuses one that belongs to another project, so this is a
    // convenience, not the check.
    fid: { type: [Number, String], default: null },
    buildingName: { type: String, default: "" },
  },
  data() {
    return {
      // The level this form is for: PTC fields in building mode, UTC in
      // component mode.
      primary: emptyFields(this.mode === "building" ? PTC_FIELDS : UTC_FIELDS),
      // The optional first component of a new building. Unused in component
      // mode, where `primary` already holds the UTC.
      secondary: emptyFields(UTC_FIELDS),
      componentOpen: false,
      options: {
        generalUnitTypes: [],
        projectTypes: [],
        statuses: [],
      },
      loadingOptions: false,
      saving: false,
      error: null,
      // { pid, fid, uid, warnings } once saved.
      created: null,
    };
  },
  computed: {
    projectStore() {
      return useProjectStore();
    },
    isBuilding() {
      return this.mode === "building";
    },
    primaryFields() {
      return this.isBuilding ? PTC_FIELDS : UTC_FIELDS;
    },
    utcFields() {
      return UTC_FIELDS;
    },
    buildingLabel() {
      return this.buildingName ? `${this.buildingName} (${this.fid})` : String(this.fid || "—");
    },
    // Saving nothing is allowed — a placeholder building is a real need while a
    // site is being worked out — but the footer says so rather than letting it
    // look like a filled-in form.
    isEmpty() {
      return (
        !Object.keys(compactFields(this.primary)).length &&
        !(this.isBuilding && this.componentOpen && Object.keys(compactFields(this.secondary)).length)
      );
    },
  },
  created() {
    // Only when a rendered field actually needs a vocabulary — see the same
    // note in NewPresaleForm. Building mode renders the UTC list too, under
    // "First component", so both are checked.
    const rendered = this.isBuilding ? PTC_FIELDS.concat(UTC_FIELDS) : UTC_FIELDS;
    if (rendered.some((field) => field.options)) this.loadOptions();
  },
  methods: {
    async loadOptions() {
      this.loadingOptions = true;
      try {
        this.options = await this.projectStore.fetchProjectTermOptions();
      } catch (err) {
        console.error("ProjectCollectionForm loadOptions error:", err);
      } finally {
        this.loadingOptions = false;
      }
    },

    async save() {
      if (this.saving) return;
      this.saving = true;
      this.error = null;
      try {
        let data;
        if (this.isBuilding) {
          const payload = { pid: Number(this.pid), ptc: compactFields(this.primary) };
          // Only sent when the section was opened AND filled: an untouched
          // section must not create an empty component alongside the building.
          const utc = this.componentOpen ? compactFields(this.secondary) : {};
          if (Object.keys(utc).length) payload.utc = utc;
          data = await this.projectStore.addProjectBuilding(payload);
        } else {
          data = await this.projectStore.addProjectComponent({
            pid: Number(this.pid),
            fid: Number(this.fid),
            utc: compactFields(this.primary),
          });
        }
        this.created = data;
        this.$emit("saved", data);
      } catch (err) {
        console.error("ProjectCollectionForm save error:", err);
        this.error = err.message || "Could not save.";
      } finally {
        this.saving = false;
      }
    },

    reset() {
      this.primary = emptyFields(this.isBuilding ? PTC_FIELDS : UTC_FIELDS);
      this.secondary = emptyFields(UTC_FIELDS);
      this.componentOpen = false;
      this.error = null;
      this.created = null;
    },
  },
};
</script>

<style scoped>
.pc-form {
  display: flex;
  flex-direction: column;
  max-height: 88vh;
  color: var(--rpd-ink);
  font-family: var(--rpd-font-sans);
}

.pc-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid var(--rpd-line);
}

.pc-header-left {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.pc-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  flex-shrink: 0;
  border-radius: var(--rpd-r-sm);
  background: var(--rpd-bg-2);
  color: var(--rpd-navy-600);
}

.pc-title {
  font-size: 15px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.pc-close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  flex-shrink: 0;
  padding: 0;
  border: none;
  border-radius: var(--rpd-r-sm);
  background: transparent;
  color: var(--rpd-muted);
  cursor: pointer;
}

.pc-close:hover:not(:disabled) {
  background: var(--rpd-bg-2);
  color: var(--rpd-ink);
}

.pc-body {
  padding: 16px 20px 20px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 14px;
  flex: 1;
  min-height: 0;
}

.pc-help {
  margin: 8px 0 0;
  font-size: 12px;
  line-height: 1.5;
  color: var(--rpd-muted);
}

.pc-row--pair {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.pc-row--pair > div {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.pc-label {
  margin: 0;
  font-size: 12px;
  font-weight: 600;
}

.pc-input {
  width: 100%;
  min-width: 0;
  padding: 8px 10px;
  border: 1px solid var(--rpd-line);
  border-radius: var(--rpd-r-sm);
  background: var(--rpd-white);
  color: var(--rpd-ink);
  font-size: 13px;
  font-family: inherit;
}

.pc-input:focus {
  outline: none;
  border-color: var(--rpd-navy-400);
}

.pc-input:disabled {
  background: var(--rpd-bg-2);
  color: var(--rpd-muted);
  cursor: not-allowed;
}

.pc-current {
  padding: 12px 14px;
  border: 1px solid var(--rpd-line-2);
  border-radius: var(--rpd-r-md);
  background: var(--rpd-bg);
}

.pc-current-title {
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 700;
}

.pc-current-grid {
  display: grid;
  grid-template-columns: 84px 1fr;
  gap: 4px 10px;
  margin: 0;
  font-size: 12px;
}

.pc-current-grid dt {
  color: var(--rpd-muted);
  font-weight: 600;
}

.pc-current-grid dd {
  margin: 0;
}

.pc-section {
  border: 1px solid var(--rpd-line-2);
  border-radius: var(--rpd-r-md);
  background: var(--rpd-bg);
}

.pc-section-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
  padding: 10px 14px;
  border: none;
  border-radius: var(--rpd-r-md);
  background: transparent;
  color: inherit;
  font-family: inherit;
  text-align: left;
  cursor: pointer;
}

.pc-section-title {
  display: block;
  font-size: 13px;
  font-weight: 700;
}

.pc-section-note {
  display: block;
  font-size: 11px;
  color: var(--rpd-muted);
}

.pc-section-body {
  padding: 0 14px 14px;
}

.pc-alert {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 12px;
  border-radius: var(--rpd-r-md);
  font-size: 12px;
  line-height: 1.5;
}

.pc-alert-title {
  font-weight: 700;
}

.pc-warn-list {
  margin: 4px 0 0;
  padding-left: 16px;
}

.pc-alert--error {
  border: 1px solid var(--rpd-coral);
  background: rgba(242, 106, 111, 0.1);
  color: var(--rpd-coral-dark);
}

.pc-alert--warn {
  border: 1px solid var(--rpd-amber);
  background: rgba(244, 183, 64, 0.12);
  color: var(--rpd-ink-2);
}

.pc-alert--ok {
  border: 1px solid var(--rpd-line);
  background: var(--rpd-bg-2);
  color: var(--rpd-ink-2);
}

.pc-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 20px;
  border-top: 1px solid var(--rpd-line);
  background: var(--rpd-bg);
}

.pc-footer-status {
  font-size: 12px;
  color: var(--rpd-muted);
}

.pc-footer-actions {
  display: flex;
  gap: 8px;
}

.pc-cancel-btn,
.pc-save-btn {
  padding: 8px 14px;
  border-radius: var(--rpd-r-sm);
  font-size: 13px;
  font-weight: 600;
  font-family: inherit;
  white-space: nowrap;
  cursor: pointer;
}

.pc-cancel-btn {
  border: 1px solid var(--rpd-line);
  background: var(--rpd-white);
  color: var(--rpd-ink-2);
}

.pc-cancel-btn:hover:not(:disabled) {
  background: var(--rpd-bg-2);
}

.pc-save-btn {
  border: 1px solid var(--rpd-navy-700);
  background: var(--rpd-navy-700);
  color: var(--rpd-white);
}

.pc-save-btn:hover:not(:disabled) {
  background: var(--rpd-navy-800);
}

.pc-cancel-btn:disabled,
.pc-save-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

@media (max-width: 575px) {
  .pc-row--pair {
    grid-template-columns: 1fr;
  }

  .pc-footer {
    flex-direction: column;
    align-items: stretch;
  }

  .pc-footer-actions {
    flex-direction: column-reverse;
  }
}

html.dark .pc-form {
  color: #e5e7eb;
}

html.dark .pc-header,
html.dark .pc-footer {
  border-color: #2b3441;
}

html.dark .pc-footer {
  background: #161c25;
}

html.dark .pc-icon,
html.dark .pc-close:hover:not(:disabled) {
  background: #232c38;
  color: #e5e7eb;
}

html.dark .pc-help,
html.dark .pc-footer-status,
html.dark .pc-close,
html.dark .pc-current-grid dt,
html.dark .pc-section-note {
  color: #9aa4b2;
}

html.dark .pc-input {
  background: #1b222c;
  border-color: #2b3441;
  color: #e5e7eb;
}

html.dark .pc-input:focus {
  border-color: var(--rpd-navy-300);
}

html.dark .pc-input:disabled {
  background: #232c38;
  color: #9aa4b2;
}

html.dark .pc-current,
html.dark .pc-section {
  background: #1b222c;
  border-color: #2b3441;
}

html.dark .pc-cancel-btn {
  background: #232c38;
  border-color: #2b3441;
  color: #e5e7eb;
}

html.dark .pc-cancel-btn:hover:not(:disabled) {
  background: #2b3441;
}

html.dark .pc-alert--warn {
  color: #f2d9a3;
}

html.dark .pc-alert--ok {
  background: #232c38;
  border-color: #2b3441;
  color: #e5e7eb;
}
</style>
